import {CheckoutCreateError, CheckoutCreateRequest, CheckoutCreateResponse} from '@shared/models/checkout';
import {Location} from '@shared/models/location';
import {Platform} from 'react-native';
import {createUrl} from '@ui/helpers/url';
import {getScreen} from '@ui/provider/navigation/screens';
import useCallable from '@ui/hooks/useCallable';
import useCrouton from '@ui/hooks/useCrouton';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';

const errorPrefix = 'competition.join.error.';
const errorToTranslationKey = (error: CheckoutCreateError) => {

    switch (error) {
        case CheckoutCreateError.UserAlreadyRegistered:
            return `${errorPrefix}userAlreadyRegistered`;
        case CheckoutCreateError.CompetitionFull:
            return `${errorPrefix}competitionFull`;
        case CheckoutCreateError.CompetitionClosed:
            return `${errorPrefix}competitionClosed`;
        case CheckoutCreateError.LocationNotAllowed:
            return `${errorPrefix}locationNotAllowed`;
        case CheckoutCreateError.MissingLocation:
            return `${errorPrefix}missingLocation`;
        case CheckoutCreateError.TeamMissing:
            return `${errorPrefix}teamMissing`;
        case CheckoutCreateError.TeamDoesNotExist:
            return `${errorPrefix}teamDoesNotExist`;
        case CheckoutCreateError.UserNotInTeam:
            return `${errorPrefix}teamAlreadyRegistered`;
        case CheckoutCreateError.TeamDoesNotHaveEnoughMembers:
            return `${errorPrefix}teamDoesNotHaveEnoughMembers`;
        default:
            return `${errorPrefix}unknown`;
    }
};

const useCheckout = (competitionId?: string) => {
    const [createCheckoutSession, isCreateCheckoutSessionLoading] = useCallable<CheckoutCreateRequest, CheckoutCreateResponse>('checkoutSession-create');
    const [checkoutError, setCheckoutError] = useState<CheckoutCreateError>();
    const crouton = useCrouton();
    const {t} = useTranslation();

    const onCreateCheckoutSession = async(teamId?: string, location?: Location) => {
        if(!competitionId){
            return;
        }

        setCheckoutError(undefined);
        const response = await createCheckoutSession({
            competitionId,
            client: Platform.OS === 'web' ? 'web' : 'mobile',
            successUrl: createUrl(getScreen('checkoutSuccess').replace('[id]', competitionId)),
            cancelUrl: createUrl(getScreen('checkoutCancel').replace('[id]', competitionId)),
            version: 'v2',
            ...(location && {location}),
            ...(teamId && {teamId}),
        });

        if(!response) {
            return;
        }

        const {error} = response;

        if(error){
            setCheckoutError(error);
            crouton.show({
                title: t('actions.error'),
                message: t(errorToTranslationKey(error)),
                preset: error === CheckoutCreateError.TeamMissing ? 'success' : 'error',
            });
        }

        return response;
    };

    return {
        onCreateCheckoutSession,
        isCreateCheckoutSessionLoading,
        checkoutError,
    };
};

export default useCheckout;
